import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ABI_ERC20_STAKING, B3_TOKEN, eqci, getExplorerTxUrl } from "../../../anyspend/index.js";
import { Button, GlareCardRounded, Input, StyleRoot, TextLoop, toast, useHasMounted, useModalStore, useSimBalance, useUnifiedChainSwitchAndExecute } from "../../../global-account/react/index.js";
import { PUBLIC_BASE_RPC_URL } from "../../../shared/constants/index.js";
import { formatTokenAmount } from "../../../shared/utils/number.js";
import invariant from "invariant";
import { ArrowRight, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { createPublicClient, encodeFunctionData, erc20Abi, http } from "viem";
import { base } from "viem/chains";
import { useAccount, useWaitForTransactionReceipt } from "wagmi";
import { AnySpendCustom } from "./AnySpendCustom.js";
import { EthIcon } from "./icons/EthIcon.js";
import { SolIcon } from "./icons/SolIcon.js";
import { UsdcIcon } from "./icons/USDCIcon.js";
const basePublicClient = createPublicClient({
  chain: base,
  transport: http(PUBLIC_BASE_RPC_URL)
});
const ERC20Staking = "0xbf04200be3cbf371467a539706393c81c470f523";
function generateEncodedDataForStakingB3(amount, beneficiary) {
  invariant(BigInt(amount) > 0, "Amount must be greater than zero");
  const encodedData = encodeFunctionData({
    abi: ABI_ERC20_STAKING,
    functionName: "stake",
    args: [BigInt(amount), beneficiary]
  });
  return encodedData;
}
export function AnySpendStakeB3({
  loadOrder,
  mode = "modal",
  recipientAddress,
  stakeAmount,
  onSuccess,
  senderAddress,
  slots,
  content,
  theme,
  showFiatOption
}) {
  const hasMounted = useHasMounted();
  const {
    setB3ModalOpen
  } = useModalStore();
  // Wagmi hooks for direct staking
  const {
    address
  } = useAccount();
  const {
    switchChainAndExecute,
    isSwitchingOrExecuting
  } = useUnifiedChainSwitchAndExecute();
  // Fetch B3 token balance
  const {
    data: simBalance,
    isLoading: isBalanceLoading
  } = useSimBalance(address, [base.id]);
  const b3RawBalanceStr = simBalance?.balances.find(b => eqci(b.address, B3_TOKEN.address))?.amount || "0";
  const b3RawBalance = BigInt(b3RawBalanceStr);
  const b3Balance = formatTokenAmount(b3RawBalance, B3_TOKEN.decimals);
  // State for direct staking flow
  const [isStaking, setIsStaking] = useState(false);
  const [stakingTxHash, setStakingTxHash] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // Wait for transaction confirmation
  const {
    isLoading: isTxPending,
    isSuccess: isTxSuccess
  } = useWaitForTransactionReceipt({
    hash: stakingTxHash,
    query: {
      structuralSharing: false // Disable to avoid BigInt serialization issues
    }
  });
  // Show success modal when transaction is confirmed
  useEffect(() => {
    if (isTxSuccess && stakingTxHash) {
      setShowAmountPrompt(false);
      setShowSuccessModal(true);
      setIsStaking(false);
    }
  }, [isTxSuccess, stakingTxHash]);
  const [userStakeAmount, setUserStakeAmount] = useState(stakeAmount || "");
  const [showAmountPrompt, setShowAmountPrompt] = useState(!stakeAmount);
  const [isAmountValid, setIsAmountValid] = useState(!!stakeAmount);
  const [validationError, setValidationError] = useState("");
  // Store display amount for UI
  const [displayAmount, setDisplayAmount] = useState("");
  // Debounced state for balance checks and messaging
  const [debouncedAmount, setDebouncedAmount] = useState("");
  const [debouncedUserStakeAmount, setDebouncedUserStakeAmount] = useState("");
  // Debounce the amount for balance checks
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAmount(displayAmount);
      setDebouncedUserStakeAmount(userStakeAmount);
    }, 500);
    return () => clearTimeout(timer);
  }, [displayAmount, userStakeAmount]);
  useEffect(() => {
    if (stakeAmount) {
      setUserStakeAmount(stakeAmount);
      setShowAmountPrompt(false);
      setIsAmountValid(true);
    }
  }, [stakeAmount]);
  const validateAndSetAmount = value => {
    // Allow decimal input by validating against a pattern
    // This regex allows numbers with up to 18 decimal places
    const isValidFormat = /^(\d+\.?\d{0,18}|\.\d{1,18})$/.test(value) || value === "";
    if (!isValidFormat && value !== "") {
      return;
    }
    setDisplayAmount(value);
    try {
      if (value === "" || value === ".") {
        setUserStakeAmount("");
        setIsAmountValid(false);
        setValidationError("");
        return;
      }
      // For UI validation - check if it's a positive number
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue <= 0) {
        setIsAmountValid(false);
        setUserStakeAmount("");
        setValidationError("Please enter a valid positive number");
        return;
      }
      // Check minimum stake amount (50 B3)
      if (numValue < 50) {
        setIsAmountValid(false);
        setUserStakeAmount("");
        setValidationError("Minimum stake amount is 50 B3");
        return;
      }
      // Convert to wei (multiply by 10^18)
      // Handle decimal places correctly by removing the decimal point
      let fullAmount;
      if (value.includes(".")) {
        const [whole, fraction = ""] = value.split(".");
        // Pad with zeros to 18 decimal places
        const paddedFraction = fraction.padEnd(18, "0");
        fullAmount = whole + paddedFraction;
      } else {
        fullAmount = value + "000000000000000000"; // Add 18 zeros
      }
      // Remove leading zeros
      fullAmount = fullAmount.replace(/^0+/, "") || "0";
      // Set the full amount for the actual transaction
      setUserStakeAmount(fullAmount);
      setIsAmountValid(true);
      setValidationError("");
    } catch (error) {
      setIsAmountValid(false);
      setUserStakeAmount("");
      setValidationError("Please enter a valid amount");
    }
  };
  const handleDirectStaking = async () => {
    if (!address || !basePublicClient || !userStakeAmount) return;
    try {
      setIsStaking(true);
      // Check current allowance
      const allowance = await basePublicClient.readContract({
        address: B3_TOKEN.address,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, ERC20Staking]
      });
      // If allowance is insufficient, request approval first
      if (allowance < BigInt(userStakeAmount)) {
        toast.info("Approving B3 spending...");
        const approvalData = encodeFunctionData({
          abi: erc20Abi,
          functionName: "approve",
          args: [ERC20Staking, BigInt(userStakeAmount)]
        });
        const approvalHash = await switchChainAndExecute(base.id, {
          to: B3_TOKEN.address,
          data: approvalData,
          value: BigInt(0)
        });
        if (!approvalHash) {
          toast.error("Approval failed. Please try again.");
          return;
        }
        const approvalReceipt = await basePublicClient.waitForTransactionReceipt({
          hash: approvalHash,
          confirmations: 1
        });
        if (approvalReceipt?.status !== "success") {
          toast.error("Approval failed. Please try again.");
          return;
        }
      }
      // Execute the stake
      toast.info("Staking B3...");
      const stakeData = encodeFunctionData({
        abi: ABI_ERC20_STAKING,
        functionName: "stake",
        args: [BigInt(userStakeAmount), recipientAddress]
      });
      const stakeHash = await switchChainAndExecute(base.id, {
        to: ERC20Staking,
        data: stakeData,
        value: BigInt(0)
      });
      if (stakeHash) {
        setStakingTxHash(stakeHash);
        toast.success("Staking transaction submitted!");
      }
    } catch (error_0) {
      console.error("@@b3-stake:error:", error_0);
      toast.error("Staking failed. Please try again.");
      setShowSuccessModal(false); // Ensure modal doesn't show on error
    } finally {
      setIsStaking(false);
    }
  };
  const confirmAmount = () => {
    if (!isAmountValid) {
      toast.error("Please enter a valid amount to stake");
      return;
    }
    // Check if user has sufficient B3 balance for direct staking
    const hasEnoughBalance = b3RawBalance && BigInt(userStakeAmount) <= b3RawBalance;
    if (hasEnoughBalance) {
      // User has enough B3, proceed with direct staking
      handleDirectStaking();
    } else {
      // User needs more B3, proceed to AnySpend conversion flow
      setShowAmountPrompt(false);
    }
  };
  const header = () => _jsxs(_Fragment, {
    children: [_jsx("div", {
      className: "relative mx-auto size-32",
      children: _jsx("img", {
        alt: "b3 coin",
        className: "size-full",
        src: "https://cdn.b3.fun/b3-coin-3d.png"
      })
    }), _jsxs("div", {
      className: "from-b3-react-background to-as-on-surface-1 mt-[-60px] w-full rounded-t-lg bg-gradient-to-t",
      children: [_jsx("div", {
        className: "h-[60px] w-full"
      }), _jsx("div", {
        className: "mb-1 flex w-full flex-col items-center gap-2 p-5",
        children: _jsxs("span", {
          className: "font-sf-rounded text-2xl font-semibold",
          children: ["Swap & Staked ", userStakeAmount ? formatTokenAmount(BigInt(userStakeAmount), 18) : "", " B3"]
        })
      })]
    })]
  });
  const onFocusStakeAmountInput = () => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  };
  // Render amount input prompt if no stake amount is provided
  if (showAmountPrompt) {
    return _jsx(StyleRoot, {
      children: _jsxs("div", {
        className: "bg-b3-react-background flex w-full flex-col items-center",
        children: [_jsxs("div", {
          className: "w-full px-4",
          children: [_jsx(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0,
              ease: "easeInOut"
            },
            className: "relative mx-auto size-48",
            children: _jsx("video", {
              autoPlay: true,
              muted: true,
              playsInline: true,
              className: "size-full",
              src: "https://cdn.b3.fun/b3-sphere-to-coin.mp4"
            })
          }), _jsx(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0.1,
              ease: "easeInOut"
            },
            children: _jsx("h2", {
              className: "font-sf-rounded font-neue-montreal-medium mb-1 text-center text-2xl font-semibold",
              children: (() => {
                const hasEnoughBalance_0 = b3RawBalance && BigInt(debouncedUserStakeAmount || "0") <= b3RawBalance;
                return hasEnoughBalance_0 || !debouncedAmount ? "Stake B3" : "Swap & Stake B3";
              })()
            })
          })]
        }), _jsxs(motion.div, {
          initial: false,
          animate: {
            opacity: hasMounted ? 1 : 0,
            y: hasMounted ? 0 : 20,
            filter: hasMounted ? "blur(0px)" : "blur(10px)"
          },
          transition: {
            duration: 0.3,
            delay: 0.2,
            ease: "easeInOut"
          },
          className: "bg-b3-react-background w-full p-6",
          children: [_jsx("div", {
            className: "mb-2",
            children: _jsxs("div", {
              className: "flex items-center justify-between",
              children: [_jsx("p", {
                className: "text-as-primary/70 text-sm font-medium",
                children: "I want to stake"
              }), _jsxs("span", {
                className: "text-as-primary/50 flex items-center gap-1 text-sm",
                children: ["Available: ", isBalanceLoading ? _jsx(Loader2, {
                  className: "h-3 w-3 animate-spin"
                }) : `${b3Balance} B3`]
              })]
            })
          }), _jsxs("div", {
            className: "relative",
            children: [_jsx(Input, {
              onFocus: onFocusStakeAmountInput,
              type: "text",
              placeholder: "0.00",
              value: displayAmount,
              onChange: e => validateAndSetAmount(e.target.value),
              className: `h-14 px-4 text-lg ${!isAmountValid && displayAmount ? "border-as-red" : "border-b3-react-border"}`
            }), _jsx("div", {
              className: "font-pack absolute right-4 top-1/2 -translate-y-1/2 text-lg font-medium text-blue-500/70",
              children: "B3"
            })]
          }), !isAmountValid && displayAmount && _jsx("p", {
            className: "text-as-red mt-2 text-sm",
            children: validationError
          }), _jsx("div", {
            className: "mt-4",
            children: (() => {
              const hasEnoughBalance_1 = b3RawBalance && BigInt(debouncedUserStakeAmount || "0") <= b3RawBalance;
              if (!hasEnoughBalance_1 || !debouncedAmount) {
                return _jsxs("div", {
                  className: "bg-as-brand/10 flex flex-col items-center gap-2 rounded-lg p-4 pb-5",
                  children: [_jsxs("div", {
                    className: "flex items-center justify-center gap-2",
                    children: [_jsx("span", {
                      className: "text-as-primary text-sm font-semibold",
                      children: "Swap & stake from any token"
                    }), _jsxs(TextLoop, {
                      children: [_jsx(EthIcon, {
                        className: "h-8 w-8"
                      }), _jsx(SolIcon, {
                        className: "h-8 w-8"
                      }), _jsx(UsdcIcon, {
                        className: "h-8 w-8"
                      })]
                    }), _jsx(ArrowRight, {
                      className: "text-as-primary h-4 w-4"
                    }), _jsx("img", {
                      src: "https://cdn.b3.fun/b3-coin-3d.png",
                      className: "h-7 w-7",
                      alt: "B3 Token"
                    })]
                  }), _jsx("p", {
                    className: "text-as-primary/50 text-sm font-medium",
                    children: debouncedAmount ? `No problem, we'll help you swap to ${debouncedAmount} B3!` : "Not enough B3? We'll help you swap from other coins."
                  })]
                });
              }
            })()
          }), _jsx(Button, {
            onClick: confirmAmount,
            disabled: !isAmountValid || !displayAmount || isStaking || isTxPending || isSwitchingOrExecuting,
            className: "bg-as-brand hover:bg-as-brand/90 text-as-primary mt-4 h-14 w-full rounded-xl text-lg font-medium",
            children: isStaking || isSwitchingOrExecuting ? "Staking..." : isTxPending ? "Confirming..." : "Continue"
          })]
        })]
      })
    });
  }
  // Success Modal for Direct Staking
  if (showSuccessModal) {
    return _jsx(StyleRoot, {
      children: _jsxs("div", {
        className: "bg-b3-react-background flex w-full flex-col items-center",
        children: [_jsxs("div", {
          className: "w-full p-4",
          children: [_jsxs(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0,
              ease: "easeInOut"
            },
            className: "relative mx-auto mb-4 size-[120px]",
            children: [_jsx("div", {
              className: "absolute inset-0 scale-95 rounded-[50%] bg-black/30 blur-md"
            }), _jsxs(GlareCardRounded, {
              className: "overflow-hidden rounded-full border-none",
              children: [_jsx("img", {
                alt: "b3 coin",
                loading: "lazy",
                width: "64",
                height: "64",
                decoding: "async",
                "data-nimg": "1",
                className: "size-full shrink-0 bg-transparent text-transparent",
                src: "https://cdn.b3.fun/b3-coin-3d.png"
              }), _jsx("div", {
                className: "absolute inset-0 rounded-[50%] border border-white/10"
              })]
            })]
          }), _jsx(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0.1,
              ease: "easeInOut"
            },
            children: _jsxs("h2", {
              className: "font-sf-rounded mb-1 text-center text-2xl font-semibold",
              children: ["Staked ", formatTokenAmount(BigInt(userStakeAmount), 18), " B3"]
            })
          })]
        }), _jsxs(motion.div, {
          initial: false,
          animate: {
            opacity: hasMounted ? 1 : 0,
            y: hasMounted ? 0 : 20,
            filter: hasMounted ? "blur(0px)" : "blur(10px)"
          },
          transition: {
            duration: 0.3,
            delay: 0.2,
            ease: "easeInOut"
          },
          className: "bg-b3-react-background w-full p-6",
          children: [_jsx("div", {
            className: "mb-6",
            children: _jsx("a", {
              href: getExplorerTxUrl(base.id, stakingTxHash),
              target: "_blank",
              rel: "noopener noreferrer",
              className: "text-as-primary/70 hover:text-as-primary block break-all text-center font-mono text-sm underline transition-colors",
              children: "View transaction"
            })
          }), _jsx(Button, {
            onClick: () => {
              setB3ModalOpen(false);
              onSuccess?.();
            },
            className: "bg-as-brand hover:bg-as-brand/90 text-as-primary h-14 w-full rounded-xl text-lg font-medium",
            children: "Done"
          })]
        })]
      })
    });
  }
  const encodedData = generateEncodedDataForStakingB3(userStakeAmount, recipientAddress);
  return _jsx(AnySpendCustom, {
    loadOrder: loadOrder,
    mode: mode,
    recipientAddress: recipientAddress,
    orderType: "custom",
    dstChainId: base.id,
    dstToken: B3_TOKEN,
    dstAmount: userStakeAmount,
    contractAddress: ERC20Staking,
    encodedData: encodedData,
    metadata: {
      action: "stake B3"
    },
    header: header,
    onSuccess: onSuccess,
    showRecipient: true,
    senderAddress: senderAddress,
    slots: slots,
    content: content,
    theme: theme,
    showFiatOption: showFiatOption
  });
}